// Stock Management page and its dialogs. Excel files and stock type names stay Turkish.
const stock = {
  page: {
    title: 'Stock Management',
    subtitle: 'Consumables and fixtures: stock, assignments, movements and shipment tracking in one place.',
  },
  modules: {
    label: 'Stock management modules',
    stocks: 'Stocks',
    cargo: 'Shipments',
  },
  categories: {
    label: 'Category',
    all: 'All Stocks',
    allShort: 'All',
    sarf: 'Consumable',
    sarfShort: 'Consumable',
    demirbas: 'Fixture',
    unknown: 'Unspecified',
    sarfHint: 'Consumables: used-up items with an expiry date (medical supplies, cleaning, etc.).',
    demirbasHint: 'Fixtures: long-lived assets that are depreciated (equipment, furniture, etc.).',
  },
  status: {
    aktif: 'Active',
    inaktif: 'Inactive',
  },
  ownership: {
    owned: 'Company-owned (ours)',
    rented: 'Rented',
    customer: 'Customer-owned',
    loaned: 'On loan',
    other: 'Other',
  },
  common: {
    cancel: 'Cancel',
  },
  actions: {
    report: 'Usage Report',
    reportHint: 'Who got how much, how much was used and how much is left',
    assigned: 'Assigned Stock',
    assignedHint: 'Summary of the stock assigned to employees',
    types: 'Stock Types',
    typesHint: 'Add a stock type or delete an unused one',
    excel: 'Excel',
    excelAll: 'All stock',
    excelAllHint: 'Detailed list of the filtered stock, coloured by stock status.',
    excelMovements: 'Stock movements',
    excelMovementsHint: 'Every assignment and visit movement of the filtered stock.',
    add: 'New Stock',
  },
  stats: {
    total: 'Total Stock',
    totalShort: 'Total',
    active: 'Active Stock',
    activeShort: 'Active',
    inactive: 'Inactive Stock',
    inactiveShort: 'Inactive',
    low: 'Running Low',
    lowShort: 'Low',
    share: '{{percent}}% of this category',
  },
  toolbar: {
    search: 'Search by product name, info or stock type…',
    searchShort: 'Search stock…',
    typeFilter: 'Filter by stock type',
    allTypes: 'All types ({{count}})',
    clearType: 'Clear the stock type filter',
    expiring: 'Expiring soon',
    expiringHint: 'List items that expire within a month',
    low: 'Low stock',
    lowHint: 'List items whose remaining quantity is down to the minimum',
  },
  table: {
    listLabel: 'Stock list',
    empty: 'No products match the filters',
    rangeOf: 'of {{total}} items',
    ofTotal: 'out of {{total}} in total',
    lowHint: 'Remaining quantity is down to the minimum',
  },
  card: {
    remaining: 'Remaining',
    min: 'min {{min}}',
  },
  columns: {
    name: 'Product Name',
    category: 'Category',
    info: 'Info',
    customer: 'Customer',
    project: 'Project',
    ownership: 'Ownership',
    warehouse: 'Warehouse',
    status: 'Status',
    rate: 'Exchange Rate',
    currency: 'Currency',
    purchasePrice: 'Purchase Price',
    remaining: 'Remaining',
    minQuantity: 'Min Quantity',
    profitRate: 'Profit Rate (%)',
    startDate: 'In Use Since',
    quantity: 'Quantity',
    expiryDate: 'Expiry Date',
    stockType: 'Stock Type',
    depreciation: 'Depreciation',
    salePrice: 'Sale Price',
  },
  panel: {
    label: 'Selected stock',
    heading: 'Stock',
    low: 'Running low',
    edit: 'Edit',
    assign: 'Assign Stock',
    history: 'Stock Movements',
    toSarf: 'Make Consumable',
    toDemirbas: 'Make Fixture',
    categoryHint: 'Changes the stock’s category',
    activate: 'Activate',
    deactivate: 'Deactivate',
    statusHint: 'Changes the stock’s status',
  },
  form: {
    titleAdd: 'Add New Stock',
    subtitleAdd: 'Enter the stock details to add a new product',
    titleEdit: 'Edit Stock',
    subtitleEdit: 'Update the stock details',
    required: 'Required',
    optional: 'Select (optional)',
    select: 'Select',
    add: 'Add Stock',
    update: 'Update',
    sections: {
      category: 'Category',
      basic: 'Basic Information',
      stock: 'Stock & Warehouse',
      pricing: 'Pricing',
      dates: 'Dates',
      fixture: 'Fixture Details',
    },
    categoryDesc: {
      sarf: 'Consumables, items with an expiry date',
      demirbas: 'Fixed assets, equipment and long-lived items',
    },
    name: 'Product Name',
    namePlaceholder: 'Enter the product name',
    typePlaceholder: 'Select a type',
    noTypes: 'No stock types yet. Please add one under "Stock Types" first.',
    statusPlaceholder: 'Select a status',
    info: 'Info / Description',
    infoPlaceholder: 'Enter additional info',
    remaining: 'Remaining Quantity',
    minHint: 'You are warned when stock falls to this level',
    warehouse: 'Warehouse Location',
    warehousePlaceholder: 'Warehouse name or location',
    currencies: {
      TRY: 'Turkish Lira',
      USD: 'US Dollar',
      EUR: 'Euro',
    },
    profit: 'Profit Rate',
    ownership: 'Ownership',
    ownershipHint: 'Is the fixture ours, rented or the customer’s?',
    depreciation: 'Depreciation (%)',
    depreciationPlaceholder: 'Yearly depreciation rate',
    depreciationHint: 'Enter the fixture’s yearly loss of value',
    scope: {
      customer: 'Customer',
      customerFixture: 'Customer / Company',
      customerPlaceholder: 'Select a customer (optional)',
      ownCompany: '{{name}} (our company)',
      project: 'Project',
      projectPlaceholder: 'Select a project (optional)',
      projectsUnavailable: 'The project list could not be loaded.',
      ownCompanyHint: 'The fixture is ours; you can pick the project it is used in.',
      noMatchingProject: 'No project found for this customer; all projects are listed.',
    },
  },
  assign: {
    title: 'Assign Stock',
    total: 'Total Quantity',
    remaining: 'Remaining Quantity',
    employee: 'Employee',
    employeePlaceholder: 'Select an employee…',
    quantity: 'Quantity',
    quantityPlaceholder: 'Quantity to assign',
    max: 'At most: {{max}}',
    note: 'Note',
    notePlaceholder: 'Add an optional note…',
    submit: 'Assign',
  },
  history: {
    title: 'Stock Movements',
    excel: 'Download Excel',
    empty: 'No movements recorded for this stock yet.',
    columns: {
      date: 'Date',
      person: 'Employee / Visit',
      quantity: 'Quantity',
      type: 'Type',
      note: 'Note',
      action: 'Action',
    },
    types: {
      assignment: 'Employee Assignment',
      visit_usage: 'Visit Usage',
      visit_return: 'Visit Return',
      adjustment: 'Adjustment',
      return: 'Return',
      general: 'General',
    },
    visit: 'Visit: {{patient}}',
    noPatient: 'No patient ID',
    unknownEmployee: 'Unknown employee',
    undo: 'Undo',
    undoHint: 'Undo the assignment: the quantity returns to stock',
    visitMovement: 'Visit movement',
    noAction: 'No action',
    confirmUndo: {
      title: 'Undo assignment',
      message: '{{quantity}} × {{stock}} will be taken back and the movement record deleted. Continue?',
    },
  },
  types: {
    title: 'Stock Types',
    subtitle: 'Add a new stock type; a type without products can be deleted.',
    name: 'Stock Type Name',
    placeholder: 'E.g. Electronics, Food, Cleaning…',
    add: 'Add',
    existing: 'Existing stock types ({{count}})',
    inUseHint: 'Number of products of this type; a type with products cannot be deleted',
    delete: 'Delete "{{name}}"',
    help: 'The number is how many products have that type. Types without products can be deleted with ×.',
    confirmDelete: {
      title: 'Delete stock type',
      message: 'Are you sure you want to delete the stock type "{{name}}"?',
      confirm: 'Delete',
    },
  },
  assigned: {
    title: 'Assigned Stock',
    subtitle: 'Summary of the stock assigned to employees',
    loading: 'Loading assignments…',
    summary: {
      label: 'Assignment summary',
      total: 'Total Assignments',
      employees: 'Employees',
      none: 'No Assignment',
      products: 'Different Products',
      quantity: 'Total Quantity',
    },
    searchEmployee: 'Search by employee or stock name…',
    searchStock: 'Search by stock or employee name…',
    views: {
      label: 'View',
      employee: 'By Employee',
      stock: 'By Stock',
    },
    filters: {
      label: 'Filter employees',
      all: 'All',
      assigned: 'With assignments',
      unassigned: 'Without assignments',
    },
    empty: {
      title: 'No records found',
      search: 'No records match the search.',
      allAssigned: 'Every employee has at least one stock assigned.',
      noneYet: 'No stock has been assigned to any employee yet.',
      stockTitle: 'No assignments found',
      stockNone: 'No stock has been assigned yet.',
    },
    employeeMeta: '{{count}} assignments • {{quantity}} pcs in total',
    none: 'No assignment',
    noItems: 'No stock assigned to this employee yet',
    stockMeta: '{{count}} employees • {{quantity}} pcs handed out',
    units: '{{quantity}} pcs',
  },
  report: {
    title: 'Stock Usage Report',
    subtitle: 'Who got how much, how much was used and how much is left',
    summary: {
      label: 'Report summary',
      assigned: 'Handed out',
      used: 'Used',
      onHand: 'In the field',
      remaining: 'Left in stock',
    },
    tabs: {
      label: 'Report type',
      employee: 'Employees',
      customer: 'Customers',
      project: 'Projects',
      stock: 'Materials',
    },
    columns: {
      employee: {
        employeeName: 'Employee',
        assigned: 'Given',
        used: 'Used',
        onHand: 'On hand',
        usageRate: 'Usage',
        visitCount: 'Visits',
        perVisit: 'Per visit',
      },
      customer: {
        label: 'Customer',
        assigned: 'Sent',
        used: 'Used',
        onHand: 'In the field',
        remaining: 'Left in stock',
        usageRate: 'Usage',
        stockCount: 'Materials',
      },
      project: {
        label: 'Project',
        assigned: 'Sent',
        used: 'Used',
        onHand: 'In the field',
        remaining: 'Left in stock',
        usageRate: 'Usage',
        stockCount: 'Materials',
      },
      stock: {
        stockName: 'Material',
        customerName: 'Customer',
        projectName: 'Project',
        assigned: 'Handed out',
        used: 'Used',
        remaining: 'Left in stock',
        employeeCount: 'People',
      },
    },
    onlyConsumables: 'Consumables only',
    onlyConsumablesHint: 'Report consumables only',
    startDate: 'Start date',
    endDate: 'End date',
    search: 'Search…',
    excel: 'Excel',
    careful: '{{name}} uses the least — {{amount}} pcs per visit',
    heavy: '{{name}} uses the most — {{amount}} pcs per visit',
    loading: 'Preparing the report…',
    empty: 'No records in the selected period.',
    records_one: '{{count}} record',
    records_other: '{{count}} records',
    hint: 'The lower the average per visit, the more economical the usage.',
    critical: 'Critical',
    usedRate: '{{percent}} used',
  },
  toast: {
    added: 'Stock added!',
    addError: 'The stock could not be added: {{message}}',
    selectToEdit: 'Please select a stock to edit',
    updated: 'Stock updated!',
    updateError: 'The stock could not be updated: {{message}}',
    selectToToggleStatus: 'Please select a stock to change its status',
    statusUpdated: 'Stock status set to {{status}}!',
    statusError: 'The status could not be changed: {{message}}',
    selectToToggleCategory: 'Please select a stock to change its category',
    categorySarf: 'Stock set to "Consumable"!',
    categoryDemirbas: 'Stock set to "Fixture"!',
    categoryError: 'The category could not be changed: {{message}}',
    employeesError: 'Employees could not be loaded',
    selectToAssign: 'Please select a stock to assign',
    selectEmployee: 'Please select an employee',
    invalidQuantity: 'Please enter a valid quantity',
    insufficient: 'Not enough stock! Remaining: {{remaining}}',
    assigned: '{{quantity}} × {{stock}} assigned to {{employee}}',
    assignError: 'The stock movement could not be saved: {{message}}',
    historyError: 'Stock history could not be loaded',
    noMovementsToExport: 'There are no stock movements to export',
    historyExported: 'Stock movements Excel file downloaded!',
    excelError: 'The Excel file could not be created',
    noFilteredStocks: 'No stock matches the selected filters',
    loadingMovements: 'Loading stock movements...',
    noMovementsForFiltered: 'No movements found for the filtered stock',
    movementsExported: '{{count}} stock movements exported to Excel!',
    stocksExported: '{{count}} stock items exported to Excel!',
    selectToHistory: 'Please select a stock to see its history',
    undone: '{{quantity}} pcs taken back',
    undoError: 'The stock could not be taken back: {{message}}',
    duplicatesCleaned: '{{count}} duplicate stock types removed',
    typesError: 'Stock types could not be loaded',
    defaultTypesAdded: 'Default stock types added!',
    defaultTypesError: 'Default stock types could not be added',
    typeNameRequired: 'Please enter a stock type name',
    typeExists: 'This stock type already exists',
    typeAdded: 'Stock type added!',
    typeAddError: 'The stock type could not be added: {{message}}',
    assignedError: 'Assigned stock could not be loaded',
    typeInUse: 'This stock type has products and cannot be deleted!',
    typeDeleted: 'Stock type deleted!',
    typeDeleteError: 'The stock type could not be deleted: {{message}}',
    loadError: 'Stock data could not be loaded',
    lowStock_one: '⚠️ {{count}} product is at or below its minimum stock level!',
    lowStock_other: '⚠️ {{count}} products are at or below their minimum stock level!',
    excelDownloaded: 'Excel file downloaded ({{count}} records)',
    excelDownloadError: 'The Excel file could not be downloaded',
  },
};

export default stock;
